import React, { useCallback, useState } from 'react';
import * as Tabs from '@radix-ui/react-tabs';

import { Brand } from 'common/features/store/duck/search-generic/utils/searchResultBrandsToBrands';

import { useRootSelector } from 'common/features/featuresReducer';
import {
    selectIsAffiliateConfirmationWebEnabled,
    selectIsFlipgiveEnabledWeb,
} from 'common/features/store/duck/ffs';

import { InMyWallet } from './InMyWallet';
import { useCardsFromWallet } from 'hooks/useCardsFromWallet';
import { Routes } from 'routes';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';

import { Accordion, AccordionItem } from './Accordion';
import { useConfigureUrlWithAffiliateToken } from 'common/hooks/useGetAffiliateActivationUrl';
import { selectSelectedOrgId } from 'common/features/store/duck/home/duck';
import { logAffiliateClick, logPDPShopDealClick } from 'config/analytics';
import { WarningBanner } from './Banners';
import { AffiliateQueryParams } from 'common/api/crm/enums/Affiliate';
import { AffiliateBonusExpiration } from './ProductInfoBonusExpiration';
import { ExternalLinkArrow } from 'features/wallet/components/ShopOnlineBanner';
import AffiliateConfirmationModal from './AffiliateConfirmationModal';
import { getConfirmationDrawerText } from 'common/utils/confirmationDrawer';
import { Deals, DealsEarningTextHeader } from 'components/deals/Deals';

export interface AffiliateTabPanelProps {
    value: string;
    brand: Brand;
    contentStyle: string;
    brandId: number | null;
}

const DetailsAndExclusions = ({ brand }: { brand: Brand }) => (
    <div className="text-xs px-3">
        {brand.affiliateVariablePricingText ? (
            <>
                <div className="font-bold">Earnings Categories</div>
                <div
                    dangerouslySetInnerHTML={{
                        __html: brand.affiliateVariablePricingText,
                    }}
                ></div>
            </>
        ) : null}

        <>
            <div className={`font-bold ${brand.affiliateVariablePricingText ? 'mt-6' : 'mt-4'}`}>
                Exclusions
            </div>
            <p className="mt-2 mb-2">
                Using loyalty or rewards points on your purchase will void your online earnings.
            </p>
            <div
                dangerouslySetInnerHTML={{
                    __html: `${brand.affiliateExclusionText || ''}
<br /><br /><p>When you buy through the link on RaiseRight, we may earn a commission.</p>`,
                }}
            ></div>
        </>
    </div>
);

const TermsAndConditions = ({ brand }: { brand: Brand }) =>
    brand.affiliateOfferTerms ? (
        <div className="text-xs px-3">
            <div dangerouslySetInnerHTML={{ __html: brand.affiliateOfferTerms }}></div>
        </div>
    ) : null;

const termsStyles = 'text-xs font-semibold';

export const AffiliateTabPanel = ({
    value,
    brand,
    contentStyle,
    brandId,
}: AffiliateTabPanelProps) => {
    const history = useHistory();
    const orgId = useSelector(selectSelectedOrgId);
    const isFlipgiveEnabled = useRootSelector(selectIsFlipgiveEnabledWeb);
    const [activationUrl, isLoadingUrl] = useConfigureUrlWithAffiliateToken(
        '{{token}}',
        isFlipgiveEnabled ? brand?.affiliateOfferUrl : undefined,
        AffiliateQueryParams.ChannelWeb.replace('{source}', 'pdp')
    );

    const errorFetchingActivationUrl = !isLoadingUrl && !activationUrl;
    const isFetchingOrError = isLoadingUrl || !activationUrl;

    const accordionItems = [
        {
            title: <span className={termsStyles}>Earnings Details & Exclusions</span>,
            content: <DetailsAndExclusions brand={brand} />,
            value: 'details-and-exclusions',
        },
        brand.affiliateOfferTerms
            ? {
                  title: <span className={termsStyles}>Terms and Conditions</span>,
                  content: <TermsAndConditions brand={brand} />,
                  value: 'terms-and-conditions',
              }
            : undefined,
    ].filter((value) => value !== undefined) as AccordionItem[];

    const userCards = useCardsFromWallet().filter(
        (c) => c.brandId === brandId && c.cardBalance !== null && c.cardBalance > 0
    );
    const goToCardPage = (cardId: string) =>
        history.push(Routes.WalletMyCardDetails.replace(':cardId', cardId));

    const isAffiliateConfirmationEnabled = useSelector(selectIsAffiliateConfirmationWebEnabled);
    const timeframeMessage = getConfirmationDrawerText(brand?.averagePurchaseDelayHours ?? 0);
    const [isDrawerOpen, setIsDrawerOpen] = useState(false);

    const onClickShopNow = useCallback(() => {
        logAffiliateClick({
            affiliateChannel: process.env.REACT_APP_RAISE_RIGHT_APP_TYPE || 'web',
            affiliateMobilePlatform: navigator.userAgent.match(/iPhone/i)
                ? 'ios'
                : navigator.userAgent.match(/Android/i)
                ? 'android'
                : '',
            version: `react-web-${process.env.REACT_APP_VERSION}`,
            location: 'PDP',
            brandId,
            brandName: brand.name,
            orgId,
        });

        // Show modal if the feature flag is enabled
        if (isAffiliateConfirmationEnabled) {
            setIsDrawerOpen(true);
        }
    }, [brand.name, brandId, orgId, isAffiliateConfirmationEnabled]);

    const onDealActivated = useCallback((dealType: 'deal' | 'coupon') => {
        setIsDrawerOpen(true);

        if (brandId) {
            logPDPShopDealClick({
                brandId,
                brandName: brand.name,
                dealType: 'deal',
                orgId,
                version: `react-web-${process.env.REACT_APP_VERSION}`,
            })
        }
    }, [orgId, brand.name, brandId, setIsDrawerOpen]);

    return (
        <Tabs.Panel value={value}>
            <div
                className={contentStyle}
                data-cnstrc-product-detail
                data-cnstrc-item-id={brand.id}
                data-cnstrc-item-name={brand.name}
            >
                {`Earn for your organization by making a purchase on the ${brand.name} website. `}

                <a target="_blank" href={Routes.HowItWorksShopOnline}>
                    How it Works
                </a>

                {brand.canDoubleDip === false && brand.isRaiseRightBrand === true && (
                    <WarningBanner />
                )}

                <div className="mb-10 sm:mb-24 mt-12">
                    <div className="flex flex-col sm:flex-row items-center sm:justify-end text-grey-darkest text-lg">
                        <div className="flex flex-col">
                            <div className="flex sm:mr-3 items-end">
                                <span>
                                    {brand.affiliateVariablePricingText ? 'Earn up to' : 'Earn'}
                                </span>
                                <span>&nbsp;</span>

                                {brand.isAffiliateOfferOnBonus && brand.affiliateBonus && (
                                    <span className="line-through pr-1">
                                        {' '}
                                        {brand.affiliateRebate}%
                                    </span>
                                )}

                                <span
                                    className={`${
                                        brand.isAffiliateOfferOnBonus && brand.affiliateBonus
                                            ? 'text-bonus-pink'
                                            : 'text-dark-blue'
                                    } font-semibold text-2xl`}
                                >
                                    {brand.isAffiliateOfferOnBonus && brand.affiliateBonus
                                        ? `${brand.affiliateBonus}%`
                                        : `${brand.affiliateRebate}%`}
                                </span>
                            </div>
                            {brand.isAffiliateOfferOnBonus &&
                            brand.affiliateBonus &&
                            brand.affiliateBonusEnd ? (
                                <AffiliateBonusExpiration expiration={brand.affiliateBonusEnd} />
                            ) : null}
                        </div>

                        <a
                            className={`mt-4 sm:mt-0 md:px-16 px-24 py-2.5 flex items-center rounded-3xl font-semibold no-underline ${
                                isFlipgiveEnabled && !isFetchingOrError
                                    ? 'text-white bg-brand'
                                    : 'text-grey-medium bg-grey-light pointer-events-none'
                            }`}
                            onClick={onClickShopNow}
                            target="_blank"
                            href={isFlipgiveEnabled && !isFetchingOrError ? activationUrl : '#'}
                        >
                            Shop Now{' '}
                            <ExternalLinkArrow
                                className="inline ml-2 w-3 h-5"
                                color={isFlipgiveEnabled && !isFetchingOrError ? '#fff' : '#6D6E71'}
                            />
                        </a>
                        
                    </div>

                    <Deals 
                        affiliateUrl={activationUrl} 
                        earningsText={ DealsEarningTextHeader({ brand }) } 
                        onDealActivated={onDealActivated} 
                    />

                    {isAffiliateConfirmationEnabled ? (
                        <AffiliateConfirmationModal
                            brandName={brand.name}
                            timeframeMessage={timeframeMessage}
                            earningsPercentage={
                                brand.isAffiliateOfferOnBonus && brand.affiliateBonus
                                    ? `${brand.affiliateBonus}%`
                                    : `${brand.affiliateRebate}%`
                            }
                            brandLogo={brand.imageUrl} // Ensure the logo URL is part of the `brand` object
                            isOpen={isDrawerOpen}
                            onClose={() => setIsDrawerOpen(false)}
                        />
                    ) : null}

                    {!isFlipgiveEnabled || errorFetchingActivationUrl ? (
                        <span className="flex justify-center w-full text-sm mt-3 text-error text-center sm:text-right sm:justify-end">
                            We’re sorry, Shop Online is currently unavailable.
                            <br />
                            Please try again later.
                        </span>
                    ) : null}
                </div>


                {brandId && (
                    <div>
                        <InMyWallet
                            brandName={brand.name}
                            items={userCards}
                            onClick={(card) => goToCardPage(card.id)}
                        />
                    </div>
                )}

                
                <div className="w-full border-t-2 border-brand" />

                <div>
                    <Accordion
                        items={accordionItems}
                        defaultExpandedItemValue="details-and-exclusions"
                    />
                </div>
            </div>
        </Tabs.Panel>
    );
};
